import { useQueryClient } from '@tanstack/react-query'
import { routes } from '@shared/constants/routes'
import { GuestDataStore } from '@/features/shared/constants/global'
import { removeCookie } from '@/features/shared/utils/cookies'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import {
  useIsUserZipOnlySignedInUser,
  useIsVisitorOrZipOnly,
  useUserAddressParams,
} from '@/features/account/services/User/hooks'
import { useShouldConfirmAddress } from '@/features/account/services/Addresses/useShouldConfirmAddress'
import { useGuestParams, useSetGuestStoreCookie } from '@/features/account/services/GuestUser/hooks'
import { DynamicConfirmAddressModal } from '@/features/account/components/Modal/ConfirmAddressModal/DynamicConfirmAddressModal'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { type Product } from '@/features/shop/services/Product/types'
import { PDP_ADD_TO_CART } from '@/features/shop/components/ProductDetail/constants'

type ProductCommerceGateInput = Pick<Product, 'id' | 'universal_id' | 'storeId'>

type RunProductCommerceGateArgs = {
  product: ProductCommerceGateInput
  onReady: () => void
}

// runs the address/store prerequisites for ATC
export const useProductCommerceGate = () => {
  const isZipOnlyUser = useIsUserZipOnlySignedInUser()
  const isVisitor = useIsVisitorOrZipOnly()
  const { guestStore } = useGuestParams() ?? {}
  const setGuestStoreCookie = useSetGuestStoreCookie()
  const addressParams = useUserAddressParams()
  const router = useSegwayRouter()
  const queryClient = useQueryClient()
  const shouldConfirmAddress = useShouldConfirmAddress()
  const { openDialog } = useHandleDialog()

  return ({ product, onReady }: RunProductCommerceGateArgs) => {
    const shoppingStores = queryClient.getQueryData(
      shoppingStoresQueryOptions(addressParams).queryKey,
    )
    // We will need the product's store to determine if it is available at the new address.
    // We capture it here, before the user has updated their address.
    const productsStore = shoppingStores?.delivery_stores.find(
      (store) => store.id === product.storeId,
    )

    if (isVisitor) {
      openDialog(DynamicGuestAddressModal, {
        isCancellable: true,
        startChangeStep: true,
        showLoginLink: true,
        contentLabel: 'Enter your delivery address',
        subheading: 'Item availability and price may vary by location.',
        onSubmit: async ({ deliveryStores }) => {
          const storeAtNewAddress = deliveryStores.find((store) => store.id === productsStore?.id)
          if (storeAtNewAddress) {
            // If the product's store is available at the new address, set it in the guest store cookie
            setGuestStoreCookie(storeAtNewAddress)
            // Navigate to the PDP to continue adding the product via the query param
            await router.push(`${getProductPageUrl(product)}?${PDP_ADD_TO_CART}=true`)
          } else if (productsStore && !guestStore?.id) {
            // If the previous store is not available at the new address,
            // navigate to the GHP and trigger the store not available modal via query param
            await router.push(
              `${routes.GLOBAL_HOMEPAGE.url}?prevStore=${encodeURIComponent(productsStore.name)}`,
            )
            removeCookie(GuestDataStore.STORE)
          }
        },
      })
      return
    }

    if (isZipOnlyUser) {
      openDialog(DynamicGlobalAddressModal, {
        contentLabel: 'Enter your delivery address',
        subheading: 'Item availability and price may vary by location.',
        hideSavedAddresses: true,
        selectedStoreId: productsStore?.id,
        selectedStoreName: productsStore?.name,
        destinationUrl: `${getProductPageUrl(product)}?${PDP_ADD_TO_CART}=true`,
      })
      return
    }

    if (shouldConfirmAddress) {
      openDialog(DynamicConfirmAddressModal, { onContinue: onReady })
      return
    }

    onReady()
  }
}
